import { connect } from 'react-redux';
import { push } from 'redux-first-history';
import SetupNewTermModal from './component';
import { DEFAULT_ERROR } from '@rental-manager/constants/NotificationConstants';
import {
  createMoveInCosts,
  getRentalProtectionDisclaimers,
  requestCreateNewTerm,
  requestUpdateRentTerms,
} from '@rental-manager/actions/requests';
import type { ErrorInterface } from '@common/types';
import { closeNotification, showModalNotification } from '@common/actions/NotificationActions';
import { trackValidationError } from '@common/actions/TrackingActions';
import { checkRequestActionSuccess, getSignInPage } from '@common/paymentsUtils/api';
import { coerceToDate, formatDate, getPossibleEndDates } from '@common/paymentsUtils/dateUtils';
import paths from '@rental-manager/pathsManifest';
import { getDate } from 'date-fns';
import HTTP_STATUS from '@common/constants/HttpConstants';
import { generateMoveInCostPayload } from '@common/paymentsUtils/moveInCostsUtil';
import { resetNewTermSettings, saveNewTermSettings } from '@rental-manager/actions/DashboardActions';
import { logError } from '@zg-rentals/log-error';

const mapStateToProps = (state, { listing, propertyId, landlordStatus, baseUrl }) => {
  const { currentTerm, paymentScheduleInfo, newTermSettings, listingAlias } = state.payments.landlord.property;
  const { moveInCostsSettings, leasesForProperty, rentalProtectionDisclaimers, leaseId, rentGuarantee } =
    state.payments.landlord.onboarding[propertyId];
  const { paymentsEncodedLeaseId } = leasesForProperty[leaseId] || {};

  return {
    landlordEmail: state.user.email,
    currentTenantEmail: currentTerm.tenantEmail,
    listingTitle: listing.listingTitle,
    rentTermsKey: currentTerm.rentTermsKey,
    tenantFirstName: currentTerm.tenantFirstName,
    monthToMonth: currentTerm.monthToMonth,
    numberOfPayments: currentTerm.numberOfPayments,
    paymentDayOfTheMonth: getDate(coerceToDate(currentTerm.firstDueDate)!),
    currentTermEndDate: currentTerm.endDateForPayments,
    firstDueDate: currentTerm.firstDueDate,
    firstAvailableEndDate: paymentScheduleInfo.firstAvailableEndDate,
    endTermOptions: getPossibleEndDates(paymentScheduleInfo.firstAvailableEndDate),
    propertyId,
    baseUrl,
    fromDashboard: false,
    landlordStatus,
    moveInCostsSettings,
    leasesForProperty,
    rentalProtectionDisclaimers,
    newTermSettings,
    paymentsEncodedLeaseId,
    listingAlias,
  };
};

const mapDispatchToProps = (dispatch, { baseUrl, propertyId }) => ({
  getRentalProtectionDisclaimers: (listingAlias) =>
    Promise.resolve().then(() => dispatch(getRentalProtectionDisclaimers({ propertyId, listingAlias }))),
  requestUpdateCurrentTerm: async ({
    numberOfPayments,
    monthToMonth,
    rentTermsKey,
    isRentGuaranteeEnabled,
    firstPossibleDueDate,
  }) => {
    try {
      await dispatch(closeNotification());
      monthToMonth = !!monthToMonth;
      const updatePaymentLengthResponse = await dispatch(
        requestUpdateRentTerms({
          monthToMonth,
          numberOfPayments,
          propertyId,
          rentTermsKey,
        }),
      );
      await checkRequestActionSuccess(updatePaymentLengthResponse);
      if (isRentGuaranteeEnabled) {
        await dispatch(saveNewTermSettings({ firstPossibleDueDate }));
        await dispatch(push(baseUrl + paths.dashboard.inviteTenantPage));
      }
      return { error: false };
    } catch (err) {
      const error = err as ErrorInterface;
      if (error.action && error.action.payload) {
        const { status } = error.action.payload;

        // If 401 status, redirect to sign-in
        if (status === HTTP_STATUS.SC_UNAUTHORIZED) {
          window.location = getSignInPage();
          return null;
        }
      }

      logError({ error: err });
      dispatch(showModalNotification(DEFAULT_ERROR));
      return { error: true };
    }
  },
  requestCreateNewTerm: async ({
    rentAmount,
    firstDueDate,
    numberOfPayments,
    monthToMonth,
    paymentDayOfTheMonth,
    tenantFirstName,
    tenantLastName,
    tenantEmail,
    moveInCostsSettings,
    paymentsEncodedLeaseId,
  }) => {
    try {
      firstDueDate = formatDate(firstDueDate, 'yyyy-MM-dd');
      // TODO: API expects monthToMonth even if false

      monthToMonth = monthToMonth ? true : false;
      const paymentLength = monthToMonth ? { monthToMonth: true } : { numberOfPayments };
      const payload = {
        amount: rentAmount,
        firstDueDate,
        dueLastDayOfMonth: paymentDayOfTheMonth === 31,
        tenantFirstName,
        tenantLastName,
        tenantEmail,
        paymentsEncodedLeaseId,
        ...paymentLength,
      };
      const action = await dispatch(requestCreateNewTerm({ propertyId, ...payload }));
      await checkRequestActionSuccess(action);

      const newRentTermsKey = action.payload?.data?.rentTermsKey;
      const { securityDepositSettings, proratedRentSettings, otherSettings, dueDate } = moveInCostsSettings;
      const moveInCosts = generateMoveInCostPayload(moveInCostsSettings);

      const micPayload = {
        dueDate,
        moveInCosts,
        rentTermsKey: newRentTermsKey,
      };

      if (
        proratedRentSettings.proratedRentStartsOpen === true ||
        securityDepositSettings.securityDepositStartsOpen === true ||
        otherSettings.length !== 0
      ) {
        const result = await dispatch(createMoveInCosts({ propertyId, ...micPayload }));
        await checkRequestActionSuccess(result);
      }
      await dispatch(resetNewTermSettings());
      await dispatch(push({ pathname: baseUrl + paths.dashboard.postConfirmationPage, state: { tenantFirstName } }));
    } catch (err) {
      logError({ error: err });
      await dispatch(showModalNotification(DEFAULT_ERROR));
      return { error: true };
    }
  },
  onClose: async () => {
    await dispatch(closeNotification());
    await dispatch(resetNewTermSettings());
    await dispatch(push(baseUrl));
  },
  trackValidationError: (data) => dispatch(trackValidationError(data)),
});

const mergeProps = (stateProps, dispatchProps) => {
  const { moveInCostsSettings, paymentsEncodedLeaseId, paymentDayOfTheMonth } = stateProps;
  return {
    ...stateProps,
    ...dispatchProps,
    requestCreateNewTerm: ({
      rentAmount,
      firstDueDate,
      numberOfPayments,
      monthToMonth,
      tenantFirstName,
      tenantLastName,
      tenantEmail,
    }) =>
      dispatchProps.requestCreateNewTerm({
        rentAmount,
        firstDueDate,
        numberOfPayments,
        monthToMonth,
        paymentDayOfTheMonth,
        tenantFirstName,
        tenantLastName,
        tenantEmail,
        moveInCostsSettings,
        paymentsEncodedLeaseId,
      }),
    getRentalProtectionDisclaimers: () => getRentalProtectionDisclaimers(stateProps.listingAlias),
  };
};
export default connect(mapStateToProps, mapDispatchToProps, mergeProps)(SetupNewTermModal);
